import React, { useState, useEffect } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  ChevronDown,
  Eye,
  Users,
  Clock,
  ImageOff,
  CheckCircle,
  Circle,
  ThumbsUp,
} from "lucide-react";
import { Modal, Select, Pagination } from "antd";
import axios from '../../utils/axiosDash';

const { Option } = Select;

function isoToTime(isoStr) {
  if (!isoStr) return "—";
  const str = typeof isoStr === "object" ? isoStr?.toISOString?.() ?? "" : String(isoStr);
  const t = str.split("T")[1];
  return t ? t.substring(0, 8) : "—";
}

function isoToDate(isoStr) {
  if (!isoStr) return "—";
  const str = typeof isoStr === "object" ? isoStr?.toISOString?.() ?? "" : String(isoStr);
  const d = str.split("T")[0];
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
}

const levelCfg = {
  ok: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-500",
    label: "OK",
    Icon: CheckCircle2,
  },
};

const Label = ({ children, className = "" }) => (
  <span className={`text-[10px] font-bold uppercase tracking-[0.8px] text-slate-500 ${className}`}>
    {children}
  </span>
);

const Alerts = () => {
  const [levelFilter,  setLevelFilter]  = useState("all");
  const [camFilter,    setCamFilter]    = useState("All");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [dateFilter,   setDateFilter]   = useState("");
  const [ackFilter,    setAckFilter]    = useState("false");
  const [expanded,     setExpanded]     = useState(null);
  const [previewAlert, setPreviewAlert] = useState(null);
  const [alerts,       setAlerts]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [cameraList, setCameraList] = useState([]);
  const [uniqueModules, setUniqueModules] = useState([]);
  const [acknowledging, setAcknowledging] = useState(null);
  const [isLateNight, setIsLateNight] = useState(false);
  
  // Add state for counts
  const [counts, setCounts] = useState({
    unacknowledged: 0,
    acknowledged: 0
  });

  // Define dummy modules that should always be shown in the filter
  const DUMMY_MODULES = ['loitering', 'fall detection', 'smoke & fire', 'late night alert'];

  const fetchTanseeqCameras = async () => { 
    try { 
      const response = await axios.get("/fetch-tanseeq-cameraalerts"); 
      if (response.data.success) { setCameraList(response.data.cameras); } 
    } catch (error) { 
      console.error("Error fetching cameras:", error); 
    } 
  };
    
  const fetchModules = async () => {
    try {
      const response = await axios.get("/fetch-tanseeq-modules");
      if (response.data.success) {
        // Combine real modules with dummy modules
        const realModules = response.data.modules || [];
        // Remove duplicates and ensure dummy modules are included
        const allModules = [...new Set([...realModules, ...DUMMY_MODULES])];
        setUniqueModules(allModules);
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      // If API fails, still show dummy modules
      setUniqueModules(DUMMY_MODULES);
    }
  };

  const fetchLateNightAlerts = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      
      // Pass the date filter to the API
      const response = await axios.post("/fetch-late-night-alerts", {
        page,
        limit,
        isAcknowledged: ackFilter,
        date: dateFilter,  // Pass date filter
        cameraId: camFilter // Pass camera filter (though it will always be camera 4 for late night)
      });

      // console.log("Late night alerts response:", response.data);

      if (response.data.success) {
        const formattedAlerts = response.data.alerts.map((item, index) => {
          const hasImage = item.hasImage || (item.image_url && item.image_url.trim() !== '');
          
          return {
            id: item._id || index,
            time: isoToTime(item.timestamp),
            date: isoToDate(item.timestamp),
            timestamp: item.timestamp,
            cam: item.camera_id,
            camName: item.camera_name,
            location: item.camera_name || `Camera ${item.camera_id}`,
            // Use the original type from the database
            module: item.type || item.alertType || "Person Count",
            eventType: item.type || item.alertType || "Person Count",
            level: "ok",
            personCount: item.personsVisible || 0,
            personsVisible: item.personsVisible || 0,
            imgUrls: hasImage ? [{ 
              url: item.image_url, 
              time: item.timestamp,
              count: item.personsVisible 
            }] : [],
            hasImages: hasImage,
            details: item.details || {},
            uniqueEventId: item._id,
            alert: item.alert,
            isAcknowledged: item.isAcknowledged || false,
            acknowledgedAt: item.acknowledgedAt || null,
            originalType: item.type || item.alertType || "Person Count"
          };
        });

        const sorted = formattedAlerts.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        setAlerts(sorted);
        setTotalAlerts(response.data.pagination.totalRecords);
        setCurrentPage(response.data.pagination.page);
        setPageSize(response.data.pagination.limit);
      }
    } catch (error) {
      console.error("Error fetching Late Night Alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update fetchLateNightAlertCounts to pass date filter
  const fetchLateNightAlertCounts = async () => {
    try {
      const response = await axios.post("/fetch-late-night-alert-counts", {
        date: dateFilter // Pass date filter
      });

      if (response.data.success) {
        setCounts({
          unacknowledged: response.data.data.unacknowledged,
          acknowledged: response.data.data.acknowledged
        });
      }
    } catch (error) {
      console.error("Error fetching late night alert counts:", error);
    }
  };

  // New function to fetch counts
  const fetchAlertCounts = async () => {
    try {
        if (moduleFilter === "late night alert") {
        await fetchLateNightAlertCounts();
        return;
      }
      const response = await axios.post("/fetch-tanseeq-alert-counts", {
        cameraId: camFilter,
        date: dateFilter,
        module: moduleFilter,
        level: levelFilter
      });

      if (response.data.success) {
        setCounts({
          unacknowledged: response.data.data.unacknowledged,
          acknowledged: response.data.data.acknowledged
        });
      }
    } catch (error) {
      console.error("Error fetching alert counts:", error);
    }
  };

  const fetchTanseeqAlerts = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);

      // FIRST: Check if "Late Night Alert" is selected (special case)
      if (moduleFilter === "late night alert") {
        await fetchLateNightAlerts(page, limit);
        return;
      }

      // THEN: Check if other dummy modules are selected
      const isDummyModule = DUMMY_MODULES.includes(moduleFilter);
      if (isDummyModule && moduleFilter !== "All") {
        setAlerts([]);
        setTotalAlerts(0);
        setLoading(false);
        return;
      }

      // Regular alerts fetch
      const response = await axios.post("/fetch-tanseeq-alert", {
        page,
        limit,
        cameraId: camFilter,
        date: dateFilter,
        module: moduleFilter,
        level: levelFilter,
        isAcknowledged: ackFilter
      });

      if (response.data.success) {
        const formattedAlerts = response.data.alerts.map((item, index) => {
          const hasImage = item.hasImage || (item.image_url && item.image_url.trim() !== '');
          
          return {
            id: item._id || index,
            time: isoToTime(item.timestamp),
            date: isoToDate(item.timestamp),
            timestamp: item.timestamp,
            cam: item.camera_id,
            camName: item.camera_name,
            location: item.camera_name || `Camera ${item.camera_id}`,
            module: item.type || "Person Count",
            eventType: item.type || "Person Count",
            level: "ok",
            personCount: item.personsVisible || 0,
            personsVisible: item.personsVisible || 0,
            imgUrls: hasImage ? [{ 
              url: item.image_url, 
              time: item.timestamp,
              count: item.personsVisible 
            }] : [],
            hasImages: hasImage,
            details: item.details || {},
            uniqueEventId: item._id,
            alert: item.alert,
            isAcknowledged: item.isAcknowledged || false,
            acknowledgedAt: item.acknowledgedAt || null
          };
        });

        const sorted = formattedAlerts.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );

        setAlerts(sorted);
        setTotalAlerts(response.data.pagination.totalRecords);
        setCurrentPage(response.data.pagination.page);
        setPageSize(response.data.pagination.limit);
      }
    } catch (error) {
      console.error("Error fetching Tanseeq Alerts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle acknowledgment
  const toggleAcknowledgment = async (alertId) => {
    try {
      setAcknowledging(alertId);
      const response = await axios.post("/toggle-tanseeq-acknowledgment", { alertId });
      
      if (response.data.success) {
        // Refresh alerts and counts after toggling
        await Promise.all([
          fetchTanseeqAlerts(currentPage, pageSize),
          fetchAlertCounts()
        ]);
      }
    } catch (error) {
      console.error("Error toggling acknowledgment:", error);
    } finally {
      setAcknowledging(null);
    }
  };

  useEffect(() => { 
    fetchTanseeqCameras();
    fetchModules();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [camFilter, levelFilter, moduleFilter, dateFilter, ackFilter]);

  // Fetch alerts and counts when filters change
  useEffect(() => {
    fetchTanseeqAlerts(currentPage, pageSize);
    fetchAlertCounts();
  }, [currentPage, pageSize, camFilter, dateFilter, moduleFilter, levelFilter, ackFilter]);

  useEffect(() => {
    if (!previewAlert) {
      setImageLoading(false);
      return;
    }
    const current = previewAlert.imgUrls?.[previewAlert.currentIndex];
    if (!current?.url) {
      setImageLoading(false);
      return;
    }
    setImageLoading(true);
    const img = new Image();
    img.onload  = () => setImageLoading(false);
    img.onerror = () => setImageLoading(false);
    img.src = current.url;
  }, [previewAlert]);

  const filtered = alerts;

  const hasActiveFilter =
    levelFilter !== "all" || camFilter !== "All" ||
    moduleFilter !== "All" || dateFilter !== "" || ackFilter !== "false";

  // Check if a dummy module is selected
  const isDummySelected = DUMMY_MODULES.includes(moduleFilter) && moduleFilter !== "All" && moduleFilter !== "late night alert";

  return (
    <div className="flex flex-col gap-6 p-6 bg-slate-50 min-h-screen">
      {/* ── Header ── */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Label className="text-red-500">Live + Historical</Label>
          <h1 className="mt-1 text-[26px] font-bold tracking-[-0.5px] text-slate-900">
            Alert Centre
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Security events across monitored zones
          </p>
        </div>
      </div>

      {/* ── Acknowledgment Tabs/Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Unacknowledged Alerts Card */}
        <div 
          className={`rounded-2xl border-2 p-4 cursor-pointer transition-all ${
            ackFilter === "false" 
              ? "border-red-500 bg-red-50 shadow-md" 
              : "border-slate-200 bg-white hover:border-red-300 hover:shadow-sm"
          }`}
          onClick={() => setAckFilter("false")}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Not Acknowledged</p>
                <p className="text-xs text-slate-500">Alerts requiring attention</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black text-red-600">{counts.unacknowledged}</span>
              {ackFilter === "false" && (
                <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">Active</span>
              )}
            </div>
          </div>
        </div>

        {/* Acknowledged Alerts Card */}
        <div 
          className={`rounded-2xl border-2 p-4 cursor-pointer transition-all ${
            ackFilter === "true" 
              ? "border-emerald-500 bg-emerald-50 shadow-md" 
              : "border-slate-200 bg-white hover:border-emerald-300 hover:shadow-sm"
          }`}
          onClick={() => setAckFilter("true")}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Acknowledged</p>
                <p className="text-xs text-slate-500">Resolved alerts</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black text-emerald-600">{counts.acknowledged}</span>
              {ackFilter === "true" && (
                <span className="px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">Active</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Camera */}
          <div className="flex flex-col gap-1">
            <Label>Camera</Label>
            <Select 
              value={camFilter} 
              onChange={setCamFilter} 
              className="w-[180px]"
              size="middle"
            >
              <Option value="All">All Cameras</Option>
              {cameraList.map((camera) => ( <Option key={camera} value={camera}> {camera} </Option> ))}
            </Select>
          </div>

          {/* Zone Type */}
          <div className="flex flex-col gap-1">
            <Label>Zone Type</Label>
            <Select 
              value={moduleFilter} 
              onChange={setModuleFilter} 
              className="w-[180px]"
              size="middle"
            >
              <Option value="All">All Zone Types</Option>
              {uniqueModules.map((m) => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Select>
          </div>

          {/* Date Filter */}
          <div className="flex flex-col gap-1">
            <Label>Date</Label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>

          {hasActiveFilter && (
            <button
              onClick={() => {
                setLevelFilter("all");
                setCamFilter("All");
                setModuleFilter("All");
                setDateFilter("");
                setAckFilter("false");
              }}
              className="mb-0.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-[7px] text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* ── Alerts List ── */}
      <div className="flex flex-col gap-3">
        {loading && (
          <div className="py-10 text-center text-sm text-slate-500 font-medium">
            Loading alerts…
          </div>
        )}

        {!loading && isDummySelected && (
          <div className="py-16 text-center bg-white rounded-xl border border-slate-200">
            <CheckCircle2 size={48} className="mx-auto mb-3 text-emerald-400" />
            <p className="font-semibold text-slate-700">All clear! No pending alerts</p>
            <p className="text-sm text-slate-500 mt-1">All alerts have been acknowledged</p>
          </div>
        )}

        {!loading && !isDummySelected && filtered.map((a) => {
          const cfg  = levelCfg[a.level] || levelCfg.ok;
          const isOpen = expanded === a.id;
          const isAcknowledged = a.isAcknowledged;
          const isAcknowledging = acknowledging === a.id;

          return (
            <div
              key={a.id}
              className={`overflow-hidden rounded-xl border bg-white shadow-sm transition-all ${
                isAcknowledged ? 'border-emerald-200' : cfg.border
              }`}
            >
              {/* ── Main Row ── */}
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors flex-wrap"
                onClick={() => setExpanded(isOpen ? null : a.id)}
              >
                {/* Acknowledgment Status Icon */}
                <div className={`flex-shrink-0 ${isAcknowledged ? 'text-emerald-500' : 'text-red-500'}`}>
                  {isAcknowledged ? (
                    <CheckCircle size={16} />
                  ) : (
                    <Circle size={16} className="fill-red-500/20" />
                  )}
                </div>

                {/* Time */}
                <div className="w-20 flex-shrink-0">
                  <p className="font-mono text-xs font-bold text-slate-700">{a.time}</p>
                  <p className="mt-0.5 text-[10px] text-slate-500">{a.date}</p>
                </div>

                {/* Camera Name */}
                <div className="flex flex-shrink-0 items-center gap-1 rounded-lg bg-slate-100 px-3 py-1">
                  <ShieldAlert size={12} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-600">{a.cam}</span>
                </div>

                {/* Zone Type */}
                <span className={`flex-shrink-0 rounded-md px-2 py-1 text-[10px] font-bold ${
                  a.module === "Intrusion"  ? "bg-red-50 text-red-600"   :
                  a.module === "Tailgating" ? "bg-amber-50 text-amber-600" :
                  a.module === "Loitering"  ? "bg-purple-50 text-purple-600" :
                  a.module === "Fall Detection" ? "bg-orange-50 text-orange-600" :
                  a.module === "Smoke & Fire" ? "bg-rose-50 text-rose-600" :
                  a.module === "late night alert" ? "bg-indigo-50 text-indigo-600" :
                  "bg-blue-50 text-blue-600"
                }`}>
                  {a.module}
                </span>

                {/* Person Count */}
                {a.module === "Person Count" && a.personCount > 0 && (
                <div className="flex flex-shrink-0 items-center gap-1 rounded-lg bg-blue-50 border border-blue-200 px-3 py-1">
                    <Users size={11} className="text-blue-500" />
                    <span className="text-[11px] font-bold text-blue-600">
                    {a.personCount} {a.personCount === 1 ? "Person" : "Persons"}
                    </span>
                </div>
                )}

                {/* Location */}
                <span className="flex-1 truncate text-sm text-slate-700 min-w-[100px]">
                  {a.location}
                </span>

                {/* Acknowledge Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleAcknowledgment(a.id);
                  }}
                  disabled={isAcknowledging}
                  className={`flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold transition ${
                    isAcknowledged
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100'
                      : 'bg-amber-50 border border-amber-200 text-amber-600 hover:bg-amber-100'
                  } ${isAcknowledging ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isAcknowledging ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : isAcknowledged ? (
                    <ThumbsUp size={13} />
                  ) : (
                    <CheckCircle size={13} />
                  )}
                  {isAcknowledged ? 'Acknowledged' : 'Acknowledge'}
                </button>

                {/* View Snapshot / No Image */}
                {a.hasImages ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewAlert({
                        imgUrls: a.imgUrls,
                        cam: a.cam,
                        location: a.location,
                        module: a.module,
                        currentIndex: 0,
                      });
                    }}
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-[11px] font-bold text-violet-600 transition hover:bg-violet-100"
                  >
                    <Eye size={13} />
                    View Snapshot
                  </button>
                ) : (
                  <div className="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-bold text-slate-400">
                    <ImageOff size={13} />
                    No Image
                  </div>
                )}

                {/* Acknowledged badge */}
                {isAcknowledged && (
                  <span className="flex-shrink-0 rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                    ✓ Done
                  </span>
                )}

                <ChevronDown
                  size={15}
                  className={`flex-shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
                />
              </div>

              {/* ── Expanded Section ── */}
              {isOpen && (
                <div className="flex flex-col gap-5 border-t border-slate-200 bg-slate-50 px-4 py-4">
                  {/* Meta Information */}
                  <div className="flex flex-wrap gap-6">
                    <div>
                      <Label>Camera</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.cam}</p>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.location}</p>
                    </div>
                    <div>
                      <Label>Zone Type</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.module}</p>
                    </div>
                    <div>
                      <Label>Event Type</Label>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{a.eventType || a.module}</p>
                    </div>
                    <div>
                      <Label>Timestamp</Label>
                      <div className="mt-1 flex items-center gap-1">
                        <Clock size={13} className="text-slate-400" />
                        <p className="text-sm font-semibold text-slate-900">
                          {a.time} - {a.date}
                        </p>
                      </div>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <p className={`mt-1 text-sm font-semibold ${isAcknowledged ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {isAcknowledged ? '✓ Acknowledged' : '⏳ Pending Acknowledgment'}
                      </p>
                      {a.acknowledgedAt && (
                        <p className="text-[10px] text-slate-400">
                          Acknowledged at: {new Date(a.acknowledgedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Unique Event ID */}
                  {a.uniqueEventId && (
                    <div>
                      <Label>Event ID</Label>
                      <p className="mt-1 text-xs font-mono text-slate-600 break-all">{a.uniqueEventId}</p>
                    </div>
                  )}

                  {/* Image Preview in Expanded View */}
                  {a.hasImages && (
                    <div>
                      <Label>Captured Image</Label>
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewAlert({
                              imgUrls: a.imgUrls,
                              cam: a.cam,
                              location: a.location,
                              module: a.module,
                              currentIndex: 0,
                            });
                          }}
                          className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-100"
                        >
                          <Eye size={14} />
                          View Full Size Image
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {!loading && !isDummySelected && filtered.length === 0 && (
          <div className="py-16 text-center bg-white rounded-xl border border-slate-200">
            {ackFilter === "false" ? (
              <>
                <CheckCircle2 size={48} className="mx-auto mb-3 text-emerald-400" />
                <p className="font-semibold text-slate-700">All clear! No pending alerts</p>
                <p className="text-sm text-slate-500 mt-1">All alerts have been acknowledged</p>
              </>
            ) : (
              <>
                <Circle size={48} className="mx-auto mb-3 text-slate-300" />
                <p className="font-semibold text-slate-700">No acknowledged alerts</p>
                <p className="text-sm text-slate-500 mt-1">Acknowledge alerts to see them here</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Image Preview Modal ── */}
      <Modal
        open={!!previewAlert}
        onCancel={() => {
          setPreviewAlert(null);
          setImageLoading(false);
        }}
        footer={null}
        centered
        title={
          previewAlert && (
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className="text-violet-600" />
              <span className="text-sm font-semibold">
                {previewAlert.cam} - {previewAlert.location}
              </span>
              <span className="text-xs text-slate-500 ml-2">{previewAlert.module}</span>
            </div>
          )
        }
        width={800}
        styles={{ body: { padding: "20px" } }}
      >
        {previewAlert && (() => {
          const current = previewAlert.imgUrls[previewAlert.currentIndex];
          return (
            <div className="flex flex-col gap-4">
              <div className="relative min-h-[400px] bg-slate-100 rounded-lg overflow-hidden">
                {imageLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50">
                    <div className="flex flex-col items-center gap-3">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-violet-500" />
                      <span className="text-sm text-slate-500">Loading image...</span>
                    </div>
                  </div>
                )}
                <img
                  key={current.url}
                  src={current.url}
                  alt={`Security snapshot from ${previewAlert.cam}`}
                  className="w-full h-auto rounded-lg"
                  style={{ display: imageLoading ? "none" : "block" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="hidden items-center justify-center h-96 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-400">
                  <div className="text-center">
                    <ImageOff size={32} className="mx-auto mb-2 text-slate-300" />
                    <p>Image failed to load</p>
                    <p className="text-xs mt-1">URL: {current.url?.substring(0, 50)}...</p>
                  </div>
                </div>
              </div>

              {current.time && (
                <div className="flex items-center justify-between text-xs text-slate-500 px-1">
                  <span>Captured: {isoToTime(current.time)}</span>
                  {/* {current.count && <span>Persons: {current.count}</span>} */}
                </div>
              )}

              {previewAlert.imgUrls.length > 1 && (
                <div className="flex items-center justify-between gap-3 pt-2">
                  <button
                    disabled={previewAlert.currentIndex === 0}
                    onClick={() =>
                      setPreviewAlert((p) => ({ ...p, currentIndex: p.currentIndex - 1 }))
                    }
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs font-medium text-slate-500">
                    Frame {previewAlert.currentIndex + 1} of {previewAlert.imgUrls.length}
                  </span>
                  <button
                    disabled={previewAlert.currentIndex === previewAlert.imgUrls.length - 1}
                    onClick={() =>
                      setPreviewAlert((p) => ({ ...p, currentIndex: p.currentIndex + 1 }))
                    }
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 disabled:opacity-40 hover:bg-slate-50 transition"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {!loading && !isDummySelected && totalAlerts > 0 && (
        <div className="flex justify-end mt-6">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalAlerts}
            showSizeChanger
            pageSizeOptions={["25", "50", "75", "100"]}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            onShowSizeChange={(page, size) => {
              setCurrentPage(1);
              setPageSize(size);
            }}
            showTotal={(total) => `Total ${total} alerts`}
          />
        </div>
      )}
    </div>
  );
};

export default Alerts;